import React, { useState, useEffect, useRef } from "react";
import "./editors.css";
import { RcaModal, useRcaCache } from "../Report/RcaQualityPanel";
import RcaQualityPanel from "../Report/RcaQualityPanel";
import {
  McButton,
  McModal,
  McLoadingIndicator,
  McTag,
} from "@maersk-global/mds-react-wrapper/components-core";
import { rcaMinScoreForPLATFORMREVIEW } from "../../config";
import { ApprovalWorkflowServices } from "../../services/approvalWorkflowServices";
import { useMsal } from "@azure/msal-react";
import Collaborators from "../Collaborators/Collaborators";
import RootCause from "../RootCause/RootCause";
import PreventiveAndCorrectiveMeasures from "../PreventiveAndCorrectiveMeasures/PreventiveAndCorrectiveMeasures";
import { UseEditors } from ".";
import IncidentSummary from "../IncidentSummary/IncidentSummary";
import BusinessImpactSection from "../IncidentSummary/impact/BusinessImpactSection";
import PrecedingEventsSection from "../IncidentSummary/preceding_events/PrecedingEventsSection";
import Recovery from "../RootCause/Recovery/Recovery";
import UserInfo from "../../context/UserAccessContext";
import { postmortemSectionTourSteps } from "../../constants/tourSteps";
import { postmortemApiEndpoint } from "../../config";
import { useQueryClient } from "react-query";
import { cachedRequest } from "../../Utils/cachedRequest";

const VERDICT_TAG_APPEARANCE = {
  Excellent:    "success",
  Strong:       "success",
  Adequate:     "info",
  "Needs work": "warning",
  "Needs Work": "warning",
  Poor:         "error",
};


function Editors({ incidentID, postmortemID, incomplete, edSignedOff, onSaveComplete }) {

  const [approvalStatus, setApprovalStatus] = useState(null);
  const approvalWorkflowService = ApprovalWorkflowServices();
  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();

  const {
    postmortemState,
    memoizedDispatch,
    saveData,
    autoSaveSection,
    saveAllSections,
    openModal,
    handleModalClose,
    collaborators,
    setCollaborators,
    handleDataSubmit,
    setImagesSavedSuccessfully,
    uploading,
    dataIsSubmittedSuccessfully,
    viewReport,
    rcaChecking,
    rcaBlockOpen,
    rcaBlockData,
    setRcaBlockOpen,
    rcaQualityData,
    getRcaQuality,
    fetchEnhancement,
  } = UseEditors(incidentID, postmortemID, edSignedOff);

  const viewReportPage = () => {
    const url = `${process.env.REACT_APP_REDIRECT_URL}/report?incidentID=${incidentID}&postmortemID=${postmortemID}`;
    window.open(url, "_blank");
  };

  // Fetch approval status to determine button visibility.
  // The initial state load (null -> value) reuses the cached response; a real state transition forces a fresh fetch.
  const currentWorkflowState = postmortemState?.postmortemCurrentStatus?.currentState;
  const previousWorkflowStateRef = useRef(currentWorkflowState);
  useEffect(() => {
    const previousState = previousWorkflowStateRef.current;
    previousWorkflowStateRef.current = currentWorkflowState;
    const stateTransitioned = previousState != null && previousState !== currentWorkflowState;

    const fetchApprovalData = async () => {
      if (!postmortemID) return;
      
      try {
        const status = await cachedRequest(
          queryClient,
          ["approvalStatus", postmortemID],
          () => approvalWorkflowService.getApprovalStatus(postmortemID),
          stateTransitioned ? { staleTime: 0 } : undefined
        );
        setApprovalStatus(status);
      } catch (error) {
        console.error("Failed to fetch approval status:", error);
      }
    };

    fetchApprovalData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postmortemID, currentWorkflowState]);

  const { userIsAdmin } = UserInfo();

  const { data: rcaData } = useRcaCache(postmortemID);
  const findInsight = (keywords) =>
    rcaData?.detailedInsights?.find((ins) =>
      keywords.some((k) => ins.key?.toLowerCase().includes(k))
    );
  const findDim = (keywords) =>
    rcaData?.dims?.find((d) =>
      keywords.some((k) => d.label?.toLowerCase().includes(k))
    );

  const submitMode =
    !userIsAdmin &&
      postmortemState?.postmortemCurrentStatus?.currentState !== "DRAFT"
      ? "saving"
      : "submitting";

  const currentState = postmortemState?.postmortemCurrentStatus?.currentState;
  const currentPriority = postmortemState?.priority;
  const isP3orP4 = currentPriority === "3" || currentPriority === "4";

  const getNextStageLabel = () => {
    switch (currentState) {
      case "DRAFT":
        return "Submit for Leadership Review";
      case "LEADERSHIP_REVIEW":
        // Awaiting approval - no button (ED/TLT will approve on Report page)
        return null;
      case "DONE":
        return null;
      default:
        return null;
    }
  };

  const getApprovalMessage = () => {
    switch (currentState) {
      case "LEADERSHIP_REVIEW":
        return "⏳ Awaiting Leadership Review approval. ED or TLT must approve on the Report page.";
      default:
        return null;
    }
  };

  const nextStageLabel = submitMode === "submitting" ? getNextStageLabel() : null;
  const approvalMessage = getApprovalMessage();

  return (
    <div style={{ width: "100%" }}>
      {/* ── Collaborators ───────────────────────────── */}
      <div className="editors-collaborators-bar">
        <span className="editors-collaborators-label">Active Collaborators</span>
        <div className="editors-collaborators-body">
          <Collaborators
            postmortemCollaborators={collaborators}
            setPostmortemCollaborators={setCollaborators}
            compact={true}
          />
        </div>
      </div>

      {/* ── Save / Submit bar ───────────────────────── */}
      <div className="editors-action-bar">
        <McButton
          appearance="neutral"
          variant="outlined"
          disabled={uploading || incomplete}
          click={async () => { await saveAllSections(); onSaveComplete?.(); }}
        >
          Save
        </McButton>
        {incidentID && getRcaQuality && (
          <RcaQualityPanel
            incidentID={incidentID}
            postMortemId={postmortemID}
            fetchQuality={(id, forceRefresh) => getRcaQuality(postmortemApiEndpoint, id, forceRefresh)}
            fetchEnhancements={fetchEnhancement}
            buttonAppearance="neutral"
            buttonFit="medium"
          />
        )}
        {approvalMessage && (
          <div style={{ 
            padding: "8px 16px", 
            backgroundColor: "#FFF4E5", 
            color: "#663C00", 
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            {approvalMessage}
          </div>
        )}
        {nextStageLabel && (
          <McButton
            appearance="primary"
            trailingicon="arrow-right"
            disabled={uploading || incomplete}
            click={() => saveData("submitting")}
          >
            {nextStageLabel}
          </McButton>
        )}
      </div>

      {/* ── Sections ──────────────────────────────────── */}
      <div style={{ paddingBottom: "60px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* Incident Summary */}
          <div>
            {postmortemState && memoizedDispatch ? (
              <IncidentSummary
                incidentSummaryState={postmortemState.incidentSummaryState}
                priority={postmortemState.priority}
                PrecedingEventsState={postmortemState.PrecedingEventsState}
                DetectionAndResponseState={postmortemState.DetectionAndResponseState}
                MttdState={postmortemState.MttdState}
                MTTRespondState={postmortemState.MTTRespondState}
                MttRepairState={postmortemState.MttRepairState}
                MttRecoverState={postmortemState.MttRecoverState}
                ImpactState={postmortemState.ImpactState}
                dispatch={memoizedDispatch}
                collaborators={collaborators}
                setPostmortemCollaborators={setCollaborators}
                images={postmortemState.images}
                imageFormData={postmortemState.imageFormData}
                incidentID={incidentID}
                postmortemID={postmortemID}
                uploadedImages={postmortemState.uploadedImages}
                setImagesSavedSuccessfully={setImagesSavedSuccessfully}
                postmortemCurrentStatus={postmortemState.postmortemCurrentStatus}
                currentStep={0}
                tourSteps={postmortemSectionTourSteps.incidentSummary}
                changedFields={postmortemState.changedFileds}
                lockedFields={postmortemState.lockedFields}
                recommendedKPIs={postmortemState.recommendedKPIs}
                postMortemIsIncompleteState={
                  postmortemState.postmortemIsIncompleteState.incomplete
                }
                onAutoSave={() => autoSaveSection(0)}
                aiInsight={findInsight(["summary", "incident"])}
                aiDim={findDim(["incident summary", "summary"])}
              />
            ) : (
              <McLoadingIndicator variant="spinner" fit="large" />
            )}
          </div>

          {/* Preceding Events — legacy section, only shown when data exists */}
          {postmortemState?.incidentSummaryState?.preceEventsDescription?.replace(/<[^>]*>/g, "").trim() && (
            <div>
              <PrecedingEventsSection
                preceEventsDescription={postmortemState.incidentSummaryState.preceEventsDescription}
                incidentSummaryState={postmortemState.incidentSummaryState}
                dispatch={memoizedDispatch}
                lockedFields={postmortemState.lockedFields}
                postmortemIsIncompleteState={postmortemState.postmortemIsIncompleteState.incomplete}
                onAutoSave={() => autoSaveSection(0)}
              />
            </div>
          )}

          {/* Business Impact — legacy section, only shown when data exists */}
          {postmortemState?.incidentSummaryState?.impact?.replace(/<[^>]*>/g, "").trim() && (
            <div>
              <BusinessImpactSection
                impact={postmortemState.incidentSummaryState.impact}
                incidentSummaryState={postmortemState.incidentSummaryState}
                dispatch={memoizedDispatch}
                lockedFields={postmortemState.lockedFields}
                postmortemIsIncompleteState={postmortemState.postmortemIsIncompleteState.incomplete}
                onAutoSave={() => autoSaveSection(0)}
              />
            </div>
          )}

          {/* Recovery — legacy section, only shown when data exists */}
          {postmortemState?.RecoveryState?.recovery?.replace(/<[^>]*>/g, "").trim() && (
            <div>
              <Recovery
                RecoveryState={postmortemState.RecoveryState}
                dispatch={memoizedDispatch}
                lockedFields={postmortemState.lockedFields}
                postmortemIsIncompleteState={postmortemState.postmortemIsIncompleteState.incomplete}
                changedFields={postmortemState.changedFileds}
                setModalIsOpen={() => {}}
                onAutoSave={() => autoSaveSection(1)}
              />
            </div>
          )}

          {/* Root Cause */}
          <div>
            <RootCause
              RootCauseState={postmortemState.RootCauseState}
              dispatch={memoizedDispatch}
              RootCauseIdentificationState={
                postmortemState.RootCauseIdentificationState
              }
              RecurrenceState={postmortemState.RecurrenceState}
              collaborators={collaborators}
              setPostmortemCollaborators={setCollaborators}
              images={postmortemState.images}
              imageFormData={postmortemState.imageFormData}
              incidentID={incidentID}
              postmortemID={postmortemID}
              setImagesSavedSuccessfully={setImagesSavedSuccessfully}
              postmortemCurrentStatus={postmortemState.postmortemCurrentStatus}
              RecoveryState={postmortemState.RecoveryState}
              tourSteps={postmortemSectionTourSteps.rootCause}
              currentStep={1}
              changedFields={postmortemState.changedFileds}
              lockedFields={postmortemState.lockedFields}
              postMortemIsIncompleteState={
                postmortemState.postmortemIsIncompleteState.incomplete
              }
              onAutoSave={() => autoSaveSection(1)}
              aiInsight={findInsight(["root", "cause"])}
              aiDim={findDim(["root cause"])}
              portfolio={postmortemState.portfolio}
              mvcVulnerabilityRelated={postmortemState.mvcVulnerabilityRelated}
            />
          </div>

          {/* Preventive & Corrective Measures */}
          <div>
            <PreventiveAndCorrectiveMeasures
              PreventiveAndCorrectiveMeasuresState={
                postmortemState.PreventiveAndCorrectiveMeasuresState
              }
              dispatch={memoizedDispatch}
              collaborators={collaborators}
              setPostmortemCollaborators={setCollaborators}
              images={postmortemState.images}
              imageFormData={postmortemState.imageFormData}
              incidentID={incidentID}
              setImagesSavedSuccessfully={setImagesSavedSuccessfully}
              postmortemCurrentStatus={postmortemState.postmortemCurrentStatus}
              currentStep={2}
              jiiraTourSteps={postmortemSectionTourSteps.jira}
              pTaskTourSteps={postmortemSectionTourSteps.pTask}
              changedFields={postmortemState.changedFileds}
              lockedFields={postmortemState.lockedFields}
              onAutoSave={() => autoSaveSection(2)}
              aiInsight={findInsight(["preventive", "corrective", "action", "measure"])}
              aiDim={findDim(["preventive", "corrective"])}
            />
          </div>
        </div>{/* editors-main-col */}
      </div>{/* editors-layout */}

      {/* ── Submit / Save modal ─────────────────────── */}
      <McModal
        heading={
          uploading
            ? "Saving…"
            : dataIsSubmittedSuccessfully || viewReport
              ? "Postmortem Ready"
              : nextStageLabel || "Confirm"
        }
        open={openModal}
        dimension="small"
        backdropcloseactiondisabled={uploading}
        escapecloseactiondisabled={uploading}
        hiddenclose={uploading}
        closed={() => !uploading && handleModalClose()}
      >
        {uploading ? (
          <div className="saving-modal-body">
            <div className="saving-modal-spinner-row">
              <McLoadingIndicator variant="spinner" fit="large" />
              <p className="saving-modal-label">Saving data, please wait…</p>
            </div>

            {rcaQualityData && (
              <div className="saving-modal-quality-card">
                <div className="saving-modal-quality-header">
                  <span className="saving-modal-quality-title">AI Quality Assessment</span>
                </div>
                <div className="saving-modal-quality-body">
                  <div className="saving-modal-quality-right">
                    {rcaQualityData.verdict && (
                      <McTag
                        label={rcaQualityData.verdict}
                        appearance={VERDICT_TAG_APPEARANCE[rcaQualityData.verdict] || "neutral-weak"}
                        fit="small"
                      />
                    )}
                    {rcaQualityData.oneLine && (
                      <p className="saving-modal-quality-oneliner">{rcaQualityData.oneLine}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : dataIsSubmittedSuccessfully || viewReport ? (
          <p style={{ margin: 0 }}>
            Your postmortem has been saved. Click below to view the full report.
          </p>
        ) : (
          <>
            <p style={{ margin: "0 0 8px", fontWeight: "600" }}>
              You are about to <strong>{nextStageLabel?.toLowerCase()}</strong>. This will advance the postmortem to the next stage.
            </p>
            <p style={{ margin: 0, color: "#6b6b6b", fontSize: "13px" }}>
              Make sure all sections are complete before proceeding.
            </p>
          </>
        )}

        {!uploading && (dataIsSubmittedSuccessfully || viewReport) && (
          <McButton
            slot="primaryAction"
            appearance="primary"
            click={() => viewReportPage()}
          >
            View Report
          </McButton>
        )}

        {!uploading && !dataIsSubmittedSuccessfully && !viewReport && (
          <>
            <McButton
              slot="secondaryAction"
              appearance="neutral"
              variant="outlined"
              click={() => handleModalClose()}
            >
              Cancel
            </McButton>
            <McButton
              slot="primaryAction"
              appearance="primary"
              trailingicon="arrow-right"
              click={() => handleDataSubmit(submitMode)}
            >
              {nextStageLabel || "Confirm"}
            </McButton>
          </>
        )}
      </McModal>

      {/* RCA quality gate — shown when score is below threshold */}
      {rcaBlockOpen && rcaBlockData && (
        <RcaModal
          data={rcaBlockData}
          incidentID={incidentID}
          onClose={() => setRcaBlockOpen(false)}
          blockingMessage={`Your postmortem quality is currently "${rcaBlockData.verdict || "Needs Work"}" — address the gaps below before re-submitting for Leadership Review.`}
        />
      )}
    </div>
  );
}

Editors.whyDidYouRender = true;
export default Editors;
